import React, { forwardRef, useEffect } from "react";

const valueStyle = {
  maxWidth: "52%",
  textAlign: "right",
  wordBreak: "break-word",
};

const ThermalReceipt = forwardRef(
  (
    {
      direction,
      clinicName,
      phone,
      address,
      note,
      orderLine,
      metaLines = [],
      rows = [],
      table,
      totals = [],
      footerLines = [],
    },
    ref
  ) => {
    useEffect(() => {
      const printStyles = `
        @media print {
          @page { size: 80mm auto; margin: 0; }
          html, body { margin: 0; padding: 0; }
          body * { visibility: hidden; }
          #thermal-print-root, #thermal-print-root * { visibility: visible; }
          #thermal-print-root {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm;
            margin: 0;
            padding: 0;
          }
        }
      `;

      const styleSheet = document.createElement("style");
      styleSheet.type = "text/css";
      styleSheet.innerText = printStyles;
      document.head.appendChild(styleSheet);

      return () => {
        document.head.removeChild(styleSheet);
      };
    }, []);

    return (
      <div
        ref={ref}
        id="thermal-print-root"
        style={{
          width: "80mm",
          fontFamily: "Arial, sans-serif",
          padding: "14px",
          boxSizing: "border-box",
          direction: direction || "ltr",
          color: "#1f2937",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "12px" }}>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700 }}>{clinicName}</h2>
          {phone ? <p style={{ margin: "4px 0", fontSize: "12px" }}>{phone}</p> : null}
          {address ? (
            <p style={{ margin: "4px 0", fontSize: "11px", color: "#4b5563" }}>{address}</p>
          ) : null}
          {note ? (
            <p style={{ margin: "7px 0", fontSize: "13px", fontStyle: "italic", fontWeight: 700 }}>
              {note}
            </p>
          ) : null}
          <hr style={{ border: "1px dashed #374151", margin: "12px 0" }} />
        </div>

        {orderLine ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
              fontSize: "14px",
            }}
          >
            <strong>{orderLine.label}</strong>
            <span style={valueStyle}>{orderLine.value}</span>
          </div>
        ) : null}

        {metaLines.map((row, idx) => (
          <div
            key={`${row.label}-${idx}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
              fontSize: "14px",
              ...(row.rowStyle || {}),
            }}
          >
            <strong style={row.labelStyle || {}}>{row.label}</strong>
            <span style={{ ...valueStyle, ...(row.valueStyle || {}) }}>{row.value}</span>
          </div>
        ))}

        {rows.map((row, idx) => (
          <div
            key={`${row.label}-${idx}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
              fontSize: "14px",
              ...(row.rowStyle || {}),
            }}
          >
            <strong style={row.labelStyle || {}}>{row.label}</strong>
            <span style={{ ...valueStyle, ...(row.valueStyle || {}) }}>{row.value}</span>
          </div>
        ))}

        {table ? (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
            <thead>
              <tr>
                {table.columns.map((column) => (
                  <th
                    key={column.key}
                    style={{
                      borderBottom: "1px dashed #374151",
                      paddingBottom: "5px",
                      fontSize: "12px",
                      fontWeight: 700,
                      textAlign: column.align || "left",
                    }}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(table.rows || []).map((row, rowIdx) => (
                <tr key={rowIdx} style={{ borderBottom: "1px solid #e5e7eb" }}>
                  {table.columns.map((column) => (
                    <td
                      key={`${rowIdx}-${column.key}`}
                      style={{
                        padding: "7px 0",
                        fontSize: "12px",
                        textAlign: column.align || "left",
                      }}
                    >
                      {row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        {totals.length > 0 ? <hr style={{ border: "1px solid #9ca3af", margin: "12px 0 8px" }} /> : null}

        {totals.map((row, idx) => (
          <div
            key={`${row.label}-${idx}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "6px",
              fontSize: row.bold ? "20px" : "16px",
              fontWeight: row.bold ? 800 : 700,
              color: row.color || "#111827",
            }}
          >
            <span>{row.label}</span>
            <span>{row.value}</span>
          </div>
        ))}

        <hr style={{ border: "1px dashed #374151", margin: "12px 0" }} />

        <div style={{ textAlign: "center", color: "#4b5563" }}>
          {footerLines.map((line, idx) => (
            <p key={idx} style={{ fontSize: idx === footerLines.length - 1 ? "11px" : "12px", margin: "6px 0" }}>
              {line}
            </p>
          ))}
        </div>
      </div>
    );
  }
);

export default ThermalReceipt;
